import axios from 'axios';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/jobs`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests when available (optional for public job search)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token && token !== 'undefined' && token !== 'null') {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Job {
  id: string;
  title: string;
  description: string;
  requirements: string[];
  skillsRequired: string[];
  requiredCourses?: string[];
  minimumScore?: number;
  employerId: string;
  employerName: string;
  companyDescription?: string;
  location: string;
  remoteType: 'onsite' | 'remote' | 'hybrid';
  jobType: 'full-time' | 'part-time' | 'contract' | 'internship';
  experienceLevel: 'entry' | 'junior' | 'mid' | 'senior' | 'lead';
  salaryRange?: {
    min: number;
    max: number;
    currency: string;
  };
  benefits?: string[];
  applicationDeadline?: Date;
  startDate?: Date;
  isActive: boolean;
  isFeatured: boolean;
  applicationsCount: number;
  viewsCount: number;
  tags: string[];
  category: string;
  industryCategory: string;
  competitionLevel: 'low' | 'medium' | 'high';
  createdAt: Date;
  updatedAt: Date;
  status: 'active' | 'inactive' | 'closed';
  skillWeights?: Record<string, number>;
}

export interface JobSearchFilters {
  keywords?: string;
  location?: string;
  remoteType?: string[];
  jobType?: string[];
  experienceLevel?: string[];
  salaryMin?: number;
  salaryMax?: number;
  category?: string;
  industryCategory?: string;
  skillsRequired?: string[];
  minimumMatchScore?: number;
  applicationDeadline?: Date;
  postedWithin?: number; // days
  sortBy?: 'relevance' | 'salary' | 'posted_date' | 'match_score' | 'competition';
  page?: number;
  limit?: number;
  employerId?: string;
}

export interface JobApplication {
  id: string;
  jobId: string;
  studentId: string;
  status: 'pending' | 'reviewed' | 'interview' | 'accepted' | 'rejected';
  coverLetter?: string;
  resumeUrl?: string;
  portfolioUrl?: string;
  studentScore: number;
  relevantCertifications: string[];
  feedback?: string;
  appliedAt: Date;
  updatedAt: Date;
}

class JobService {
  // Get all jobs with optional filtering
  async getJobs(filters?: JobSearchFilters): Promise<{ 
    jobs: Job[]; 
    total: number;
    searchMode: string;
    matches?: Record<string, any>;
  }> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== '') {
            if (Array.isArray(value)) {
              params.append(key, value.join(','));
            } else {
              params.append(key, value.toString());
            }
          }
        });
      }
      
      const response = await api.get(`/?${params.toString()}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching jobs:', error);
      throw new Error('Failed to fetch jobs');
    }
  }

  // Get job by ID
  async getJobById(jobId: string): Promise<Job> {
    try {
      const response = await api.get(`/${jobId}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching job:', error);
      throw new Error('Failed to fetch job');
    }
  }

  // Get featured jobs
  async getFeaturedJobs(limit: number = 6): Promise<Job[]> {
    try {
      const response = await this.getJobs({ 
        limit, 
        sortBy: 'posted_date' // or we could filter by isFeatured
      });
      return response.jobs.filter(job => job.isFeatured).slice(0, limit);
    } catch (error) {
      console.error('Error fetching featured jobs:', error);
      throw new Error('Failed to fetch featured jobs');
    }
  }

  // Get recent jobs (for live feed)
  async getRecentJobs(limit: number = 10): Promise<Job[]> {
    try {
      const response = await this.getJobs({ 
        limit, 
        sortBy: 'posted_date',
        postedWithin: 7 // last 7 days
      });
      return response.jobs;
    } catch (error) {
      console.error('Error fetching recent jobs:', error);
      throw new Error('Failed to fetch recent jobs');
    }
  }

  // Search jobs
  async searchJobs(query: string, filters?: Omit<JobSearchFilters, 'keywords'>): Promise<Job[]> {
    try {
      const searchFilters = { ...filters, keywords: query };
      const response = await this.getJobs(searchFilters);
      return response.jobs;
    } catch (error) {
      console.error('Error searching jobs:', error);
      throw new Error('Failed to search jobs');
    }
  }

  // Apply to job (requires authentication)
  async applyToJob(jobId: string, applicationData: {
    coverLetter?: string;
    resumeUrl?: string;
    portfolioUrl?: string;
  }): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.post(`/${jobId}/apply`, applicationData);
      return response.data;
    } catch (error) {
      console.error('Error applying to job:', error);
      throw new Error('Failed to apply to job');
    }
  }

  // Get user's job applications (requires authentication)
  async getMyApplications(): Promise<JobApplication[]> {
    try {
      const response = await api.get('/my/applications');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching applications:', error);
      throw new Error('Failed to fetch applications');
    }
  }

  // Get job recommendations (requires authentication)
  async getJobRecommendations(limit: number = 10): Promise<Job[]> {
    try {
      const response = await api.get(`/recommendations?limit=${limit}`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching job recommendations:', error);
      throw new Error('Failed to fetch job recommendations');
    }
  }

  // Get jobs by category
  async getJobsByCategory(category: string, limit: number = 10): Promise<Job[]> {
    try {
      const response = await this.getJobs({ 
        category, 
        limit, 
        sortBy: 'posted_date' 
      });
      return response.jobs;
    } catch (error) {
      console.error('Error fetching jobs by category:', error);
      throw new Error('Failed to fetch jobs by category');
    }
  }

  // Get jobs by industry category
  async getJobsByIndustry(industryCategory: string, limit: number = 10): Promise<Job[]> {
    try {
      const response = await this.getJobs({ 
        industryCategory, 
        limit, 
        sortBy: 'posted_date' 
      });
      return response.jobs;
    } catch (error) {
      console.error('Error fetching jobs by industry:', error);
      throw new Error('Failed to fetch jobs by industry');
    }
  }

  // Get popular jobs (high application count)
  async getPopularJobs(limit: number = 6): Promise<Job[]> {
    try {
      const response = await this.getJobs({ 
        limit: 50, // get more to sort by popularity
        sortBy: 'posted_date' 
      });
      
      // Sort by applications count and return top jobs
      return response.jobs
        .sort((a, b) => b.applicationsCount - a.applicationsCount)
        .slice(0, limit);
    } catch (error) {
      console.error('Error fetching popular jobs:', error);
      throw new Error('Failed to fetch popular jobs');
    }
  }

  // Get jobs by employer ID
  async getJobsByEmployer(employerId: string, limit: number = 20): Promise<Job[]> {
    try {
      const response = await this.getJobs({ 
        employerId, 
        limit, 
        sortBy: 'posted_date' 
      });
      return response.jobs;
    } catch (error) {
      console.error('Error fetching jobs by employer:', error);
      throw new Error('Failed to fetch jobs by employer');
    }
  }
}

const jobServiceInstance = new JobService();
export default jobServiceInstance;